import React, { useEffect, useState } from 'react';
import { X, Save } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { STAGES, PROSPECT_TYPES, actionItems, fmtDate, errMsg } from './crmConstants';
import { LeadTimeline } from './LeadTimeline';
import { LeadTasks } from './LeadTasks';
import { LeadEmails } from './LeadEmails';
import { LeadOffers } from './LeadOffers';

const TABS = [
  { id: 'overview', label: 'Panoramica' }, { id: 'timeline', label: 'Timeline' }, { id: 'tasks', label: 'Prossime azioni' },
  { id: 'emails', label: 'Email' }, { id: 'offers', label: 'Offerte' },
];

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-[10px] uppercase tracking-widest text-slate-500 mb-1">{label}</span>
    {children}
  </label>
);
const sel = 'w-full px-2 py-1.5 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm';

const DoneList = ({ done }) => {
  const items = actionItems(done);
  if (!items.length) return <p className="text-sm text-slate-500 italic">Nessuna azione registrata finora</p>;
  return (
    <ul className="grid sm:grid-cols-2 gap-1.5">
      {items.map(({ key, label, icon: Icon }) => (
        <li key={key} className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-xs ${key === 'offer_accepted' ? 'bg-emerald-500/15 text-emerald-200' : 'bg-slate-800 text-slate-200'}`} data-testid={`drawer-done-${key}`}>
          <Icon className="w-3.5 h-3.5 flex-shrink-0" />
          <span className="font-medium">{label}</span>
          <span className="ml-auto text-slate-400 whitespace-nowrap">{fmtDate(done[key].at)}{done[key].by ? ` · ${done[key].by}` : ''}</span>
        </li>
      ))}
    </ul>
  );
};

const Overview = ({ lead, api, showToast, onChanged }) => {
  const [notes, setNotes] = useState(lead.internal_notes || '');
  const [company, setCompany] = useState(lead.company || '');
  const [value, setValue] = useState(lead.estimated_value || '');
  useEffect(() => { setNotes(lead.internal_notes || ''); setCompany(lead.company || ''); setValue(lead.estimated_value || ''); }, [lead.id]);
  const save = async () => {
    try { await api.patch(`/admin/leads/${lead.id}`, { internal_notes: notes, company, estimated_value: Number(value) || 0 }); showToast('success', 'Salvato'); onChanged(); }
    catch (e) { showToast('error', errMsg(e)); }
  };
  const rows = [['Email', lead.email], ['Telefono', lead.phone], ['CEFR', lead.englishLevel], ['Ruolo', lead.role], ['Settore', lead.sector],
    ['Lingua madre', lead.nativeLanguage], ['Sorgente', lead.source], ['Creato', fmtDate(lead.created_at)]];
  return (
    <div className="space-y-4">
      <div>
        <p className="text-[10px] uppercase tracking-widest text-slate-500 mb-1.5">Azioni già fatte</p>
        <DoneList done={lead.actions_done || {}} />
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
        {rows.filter(([, v]) => v).map(([k, v]) => (
          <div key={k} className="border-b border-slate-800 pb-1.5"><p className="text-[10px] uppercase text-slate-500">{k}</p><p className="text-white break-words">{String(v)}</p></div>
        ))}
      </div>
      {lead.motivation && <div><p className="text-[10px] uppercase text-slate-500">Obiettivo</p><p className="text-sm text-white">{lead.motivation}</p></div>}
      <div className="grid grid-cols-2 gap-3">
        <Field label="Azienda"><input value={company} onChange={(e) => setCompany(e.target.value)} className={sel} data-testid="lead-company-input" /></Field>
        <Field label="Valore stimato (€)"><input type="number" value={value} onChange={(e) => setValue(e.target.value)} className={sel} data-testid="lead-value-input" /></Field>
      </div>
      <Field label="Note interne"><textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} className={sel} data-testid="lead-notes-input" /></Field>
      <div className="flex justify-end"><Button size="sm" onClick={save} className="bg-slate-700 hover:bg-slate-600" data-testid="lead-overview-save"><Save className="w-4 h-4 mr-1" /> Salva</Button></div>
    </div>
  );
};

export const LeadDrawer = ({ lead, api, team, currentUser, showToast, onClose, onChanged }) => {
  const [tab, setTab] = useState('overview');
  const [tick, setTick] = useState(0);
  const changed = () => { setTick((t) => t + 1); onChanged(); };
  const patch = async (data) => {
    if (data.status === 'lost') {
      const reason = window.prompt('Motivo della perdita (facoltativo):', '');
      if (reason === null) return;
      data.lost_reason = reason;
    }
    try { await api.patch(`/admin/leads/${lead.id}`, data); changed(); } catch (e) { showToast('error', errMsg(e)); }
  };
  const isAdmin = currentUser?.role === 'admin';
  const ownerOptions = isAdmin ? team : team.filter((u) => u.id === currentUser?.id);

  return (
    <div className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-sm flex justify-end" onClick={(e) => e.target === e.currentTarget && onClose()} data-testid="lead-detail-drawer">
      <div className="w-full max-w-2xl bg-slate-900 border-l border-slate-700 h-full flex flex-col animate-in slide-in-from-right duration-200">
        <div className="p-5 border-b border-slate-800 space-y-3">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h3 className="text-xl font-bold text-white truncate" data-testid="lead-drawer-name">{lead.name}</h3>
              <p className="text-xs text-slate-400 truncate">{lead.email}{lead.phone ? ` · ${lead.phone}` : ''}</p>
            </div>
            <button onClick={onClose} className="text-slate-400 hover:text-white" data-testid="lead-drawer-close"><X className="w-5 h-5" /></button>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <Field label="Fase">
              <select value={lead.status} onChange={(e) => patch({ status: e.target.value })} className={sel} data-testid="lead-stage-select">
                {STAGES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
            </Field>
            <Field label="Responsabile">
              <select value={lead.owner_id || ''} onChange={(e) => patch({ owner_id: e.target.value })} className={sel} data-testid="lead-owner-select">
                <option value="">Nessuno</option>
                {ownerOptions.map((u) => <option key={u.id} value={u.id}>{u.full_name || u.username}</option>)}
              </select>
            </Field>
            <Field label="Tipo prospect">
              <select value={lead.prospect_type || ''} onChange={(e) => patch({ prospect_type: e.target.value })} className={sel} data-testid="lead-type-select">
                <option value="">—</option>
                {PROSPECT_TYPES.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
              </select>
            </Field>
          </div>
          {isAdmin && team.length <= 1 && (
            <p className="text-[11px] text-amber-300/90" data-testid="owner-hint">Per assegnare il lead ad altri, crea nel tab Utenti un utente con ruolo "Gestore lead" o "Collaboratore": comparirà qui come responsabile.</p>
          )}
          <div className="flex gap-1 overflow-x-auto no-scrollbar">
            {TABS.map((t) => (
              <button key={t.id} onClick={() => setTab(t.id)} className={`px-3 py-1.5 rounded-full text-xs whitespace-nowrap ${tab === t.id ? 'bg-amber-500 text-slate-950 font-semibold' : 'text-slate-300 hover:bg-slate-800'}`} data-testid={`lead-tab-${t.id}`}>
                {t.label}{t.id === 'tasks' && lead.open_tasks ? ` (${lead.open_tasks})` : ''}
              </button>
            ))}
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-5">
          {tab === 'overview' && <Overview lead={lead} api={api} showToast={showToast} onChanged={changed} />}
          {tab === 'timeline' && <LeadTimeline lead={lead} api={api} showToast={showToast} onChanged={changed} refreshKey={tick} />}
          {tab === 'tasks' && <LeadTasks lead={lead} api={api} team={team} showToast={showToast} onChanged={changed} />}
          {tab === 'emails' && <LeadEmails lead={lead} api={api} showToast={showToast} onChanged={changed} isAdmin={isAdmin} />}
          {tab === 'offers' && <LeadOffers lead={lead} api={api} showToast={showToast} onChanged={changed} />}
        </div>
      </div>
    </div>
  );
};
